import { Button, Group, Stack, Text } from '@mantine/core';

import {
  canCloseRegistryOrder,
  canRecordRegistryQuote,
  describePartnerFee,
  isPartnerFeeBilled,
  type RegistryOrder,
  type RegistryOrderMessage,
} from '@/types/registry-documents';
import { RegistryOrderDocuments } from './RegistryOrderDocuments';

/**
 * What an opened order shows, on the report card and on the admin page
 * alike: the documents that arrived, the messages from the research desk
 * with the way to answer them, the way to record a quote accepted outside
 * the Hub, and the way to close an order that will never be completed.
 *
 * "Close order", "Record quote" and "Reply" only ask: the surface that
 * mounts the panel owns each dialog, so the dialog outlives a row that
 * leaves the list once its order is closed, or that is redrawn once a
 * reply or a quote is recorded.
 */
export function RegistryOrderPanel({
  order,
  onCloseOrder,
  onReplyToMessage,
  onRecordQuote,
  onReportContentChanged,
}: Readonly<{
  order: RegistryOrder;
  /** Open the close dialog for this order. Omitted where closing is not
   * offered. */
  onCloseOrder?: () => void;
  /** Open the reply dialog for one of the desk's messages on this order.
   * Omitted where replying is not offered. */
  onReplyToMessage?: (message: RegistryOrderMessage) => void;
  /** Open the dialog that records the desk's quote on its own. Omitted
   * where recording one is not offered. */
  onRecordQuote?: () => void;
  /** The digest was added to the report's document. Omitted where the
   * surface shows no report. */
  onReportContentChanged?: () => void;
}>) {
  const closeable = onCloseOrder !== undefined && canCloseRegistryOrder(order);
  const quotable = onRecordQuote !== undefined && canRecordRegistryQuote(order);

  return (
    <Stack gap="xs">
      <PartnerFeeLine order={order} />
      <RegistryOrderDocuments
        order={order}
        onReply={onReplyToMessage}
        onReportContentChanged={onReportContentChanged}
      />
      {quotable && (
        <Group gap="xs" justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            Quoted by email? Record it so the order is chased if it is late.
          </Text>
          <Button
            size="compact-xs"
            variant="light"
            onClick={onRecordQuote}
            data-testid={`registry-order-quote-${order.id}`}
          >
            Record quote
          </Button>
        </Group>
      )}
      {closeable && (
        <Group gap="xs" justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            Declined the quote or cancelled with the research desk? Close the order here so it stops
            counting as open.
          </Text>
          <Button
            size="compact-xs"
            variant="light"
            color="red"
            onClick={onCloseOrder}
            data-testid={`registry-order-close-${order.id}`}
          >
            Close order
          </Button>
        </Group>
      )}
    </Stack>
  );
}

/** What the order adds to Armadillo's partner fee, and whether a partner
 * assignment bills it yet. Nothing on an API that predates the fee. */
function PartnerFeeLine({ order }: Readonly<{ order: RegistryOrder }>) {
  const fee = describePartnerFee(order);
  if (!fee) {
    return null;
  }
  return (
    <Text size="xs" c="dimmed" data-testid={`registry-order-partner-fee-${order.id}`}>
      Partner fee: {fee}
      {isPartnerFeeBilled(order) ? ' · billed' : ''}
    </Text>
  );
}
